<template>
  <div id="fees" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2>{{ $route.meta.title }}</h2>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="4" md="3" lg="2">
            <i-instructions instructionKey="reports/fees"></i-instructions>
          </v-col>
        </v-row>

        <i-search-sort
          dateRangeColumnName="Invoice Date"
          dateRangeStartParameterName="searchByInvoiceDateStart"
          dateRangeEndParameterName="searchByInvoiceDateEnd"
          initialDateRange="lastSevenDays"
          :executeOnMount="true"
          :loading="fetching"
          :routeName="$route.name"
          exportButtonText="Export"
          :showExportButton="true"
          :disableExportButton="false"
          :maxExportableItems="-1"
          :resultsQuantity="fees.length"
          :fetchingExportFile="fetchingExcel"
          @searchQuery="searchQuery"
          @fetchExportFile="fetchExcel"
        />

        <i-progress-linear v-if="fetching && $vuetifyDisplay.smAndDown" />

        <template v-if="!fetching && fees.length === 0">
          <i-empty-results
            buttonText="Back to Reports"
            buttonRouteName="reports"
            :title="
              neverFetched
                ? 'Define search criteria and press Search to generate the report'
                : 'No Fees found'
            "
          />
        </template>

        <template v-else>
          <!-- mobile version -->
          <template v-if="$vuetifyDisplay.smAndDown">
            <v-card class="rounded-2 pa-4 pa-md-6 mb-6 totals-card">
              <v-row no-gutters>
                <v-col cols="12" class="py-1">
                  <span><strong>TOTALS</strong></span>
                </v-col>
              </v-row>
              <v-divider class="my-2"></v-divider>
              <v-row no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[3].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span
                    ><strong>{{
                      $filters.currency(totalTenantOwnerSupplierChargeIncTax)
                    }}</strong></span
                  >
                </v-col>
              </v-row>
              <v-row v-if="ownerControlledSupplierPlatformFee" no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[4].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span
                    ><strong>{{
                      $filters.currency(totalPlatformFeeIncTax)
                    }}</strong></span
                  >
                </v-col>
              </v-row>
              <v-row v-if="featureCreditReturnsEnabled" no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[5].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span
                    ><strong>{{
                      $filters.currency(totalCreditReturnFeeIncTax)
                    }}</strong></span
                  >
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[6].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span
                    ><strong>{{
                      $filters.currency(totalInvoiceTotalIncTax)
                    }}</strong></span
                  >
                </v-col>
              </v-row>
            </v-card>

            <v-row no-gutters>
              <v-col
                cols="12"
                v-for="item in fees"
                :key="item.invoiceNumber"
                class="mt-0 pa-0"
              >
                <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span
                        ><strong>{{ headers[0].text }}</strong></span
                      >
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span
                        ><strong>{{ item.invoiceNumber }}</strong></span
                      >
                    </v-col>
                  </v-row>

                  <v-divider class="my-2"></v-divider>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[1].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.invoiceDate)
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[2].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <router-link
                        v-if="createLinkForFee(item)"
                        class="i-a-link"
                        :to="createLinkForFee(item)"
                      >
                        <template>{{
                          $filters.text(item.referenceNumber)
                        }}</template>
                      </router-link>
                      <span v-else>{{
                        $filters.text(item.referenceNumber)
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[3].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(item.tenantOwnerSupplierChargeIncTax)
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-row v-if="ownerControlledSupplierPlatformFee" no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[4].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(item.platformFeeIncTax)
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-row v-if="featureCreditReturnsEnabled" no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[5].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(item.creditReturnFeeIncTax)
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[6].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(item.invoiceTotalIncTax)
                      }}</span>
                    </v-col>
                  </v-row>

                  <div class="text-center pt-2">
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-2"
                      @click="fetchInvoicePDF(item.type, item.objectId)"
                    >
                      <span>Export Fees Invoice</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <!-- desktop version -->
          <template v-else>
            <v-data-table
              class="pt-2 elevation-1 i-data-table p-report-table"
              :headers="headersDesktop"
              :items="fees"
              :loading="fetching"
              :items-per-page="-1"
              hide-default-footer
            >
              <template v-slot:[`item.invoiceDate`]="{ item }">
                <span>{{ $filters.formatDateTime12h(item.invoiceDate) }}</span>
              </template>

              <template v-slot:[`item.invoiceNumber`]="{ item }">
                <span>{{ item.invoiceNumber }}</span>
              </template>

              <template v-slot:[`item.referenceNumber`]="{ item }">
                <router-link
                  v-if="createLinkForFee(item)"
                  class="i-a-link"
                  :to="createLinkForFee(item)"
                >
                  <template>{{ $filters.text(item.referenceNumber) }}</template>
                </router-link>
                <span v-else>{{ $filters.text(item.referenceNumber) }}</span>
              </template>

              <template v-slot:[`item.platformFeeIncTax`]="{ item }">
                <span>{{ $filters.currency(item.platformFeeIncTax) }}</span>
              </template>

              <template v-slot:[`item.creditReturnFeeIncTax`]="{ item }">
                <span>{{ $filters.currency(item.creditReturnFeeIncTax) }}</span>
              </template>

              <template
                v-slot:[`item.tenantOwnerSupplierChargeIncTax`]="{ item }"
              >
                <span>{{
                  $filters.currency(item.tenantOwnerSupplierChargeIncTax)
                }}</span>
              </template>

              <template v-slot:[`item.invoiceTotalIncTax`]="{ item }">
                <span>{{ $filters.currency(item.invoiceTotalIncTax) }}</span>
              </template>

              <template v-slot:[`item.pdf`]="{ item }">
                <v-btn
                  icon
                  :loading="fetchingInvoicePDF"
                  v-tooltip="{
                    content: 'Fees Invoice PDF',
                    visible: $vuetifyDisplay.mdAndDown ? false : undefined
                  }"
                  @click="fetchInvoicePDF(item.type, item.objectId)"
                >
                  <v-icon class="i-btn-pdf">mdi-receipt-text-check</v-icon>
                </v-btn>
              </template>

              <template v-slot:[`body.append`]="{ headers }">
                <tr>
                  <td v-for="(header, i) in headers" :key="i">
                    {{ tableItemsGrandTotal[header.value] }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>
        </template>
      </v-col>
    </v-row>
    <v-dialog
      v-if="confirmationDialog"
      v-model="confirmationDialog"
      width="400px"
      @click:outside="confirmationDialog = false"
      @keydown.esc="confirmationDialog = false"
    >
      <i-confirmation-dialog
        action-text="Search"
        :submitting="fetching"
        text="You have not defined any search criteria! This is not recommended. Do you want to search anyway?"
        @cancel="confirmationDialog = false"
        @confirm="doSearch"
      />
    </v-dialog>
  </div>
</template>

<script>
import { COMMON_CONSTANTS } from '@/constants/Common.constants'
import { mapActions, mapGetters } from 'vuex'
import iProgressLinear from '@/components/ProgressLinear'
import iSearchSort from '@/components/SearchSort'
import { fetchFees, fetchFeesExcel } from '@/services/fees'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import moment from 'moment-timezone'
import { fetchSupplierOrderInvoicePdfUrl } from '@/services/supplierOrders'
import { fetchCreditReturnInvoicePdfUrl } from '@/services/creditReturns'
import { fetchOrderQueryInvoicePdfUrl } from '@/services/orderQueries'
import { openFile } from '@/utils/common'

export default {
  components: {
    iEmptyResults,
    iInstructions,
    iProgressLinear,
    iSearchSort,
    iConfirmationDialog
  },

  data: () => ({
    COMMON_CONSTANTS,
    fetching: false,
    fetchingExcel: false,
    fetchingInvoicePDF: false,
    neverFetched: true,
    feesAggregation: null,
    fees: [],
    confirmationDialog: false,
    queryObject: {}
  }),

  computed: {
    ...mapGetters('tenant', [
      'featureCreditReturnsEnabled',
      'ownerControlledSupplierPlatformFee',
      'usesAustralianTaxLabels'
    ]),

    headers() {
      return [
        { text: 'FEES INVOICE NUMBER', value: 'invoiceNumber' },
        { text: 'INVOICE DATE', value: 'invoiceDate' },
        { text: 'REFERENCE', value: 'referenceNumber' },
        {
          text: this.usesAustralianTaxLabels
            ? 'TRADING TERMS (INC TAX)'
            : 'TRADING TERMS',
          value: 'tenantOwnerSupplierChargeIncTax'
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'PLATFORM FEE (INC TAX)'
            : 'PLATFORM FEE',
          value: 'platformFeeIncTax',
          visible: this.ownerControlledSupplierPlatformFee
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'CREDIT RETURN FEE (INC TAX)'
            : 'CREDIT RETURN FEE',
          value: 'creditReturnFeeIncTax',
          visible: this.featureCreditReturnsEnabled
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'FEES INVOICE TOTAL (INC TAX)'
            : 'FEES INVOICE TOTAL',
          value: 'invoiceTotalIncTax'
        },
        { text: 'PDF', value: 'pdf', sortable: false }
      ]
    },

    headersDesktop() {
      return this.headers.filter((h) => h.visible !== false)
    },

    tableItemsGrandTotal() {
      return {
        referenceNumber: 'TOTAL',
        tenantOwnerSupplierChargeIncTax: this.formatCurrency(
          this.feesAggregation?.totalTenantOwnerSupplierChargeIncTax
        ),
        platformFeeIncTax: this.formatCurrency(
          this.feesAggregation?.totalPlatformFeeIncTax
        ),
        creditReturnFeeIncTax: this.formatCurrency(
          this.feesAggregation?.totalCreditReturnFeeIncTax
        ),
        invoiceTotalIncTax: this.formatCurrency(
          this.feesAggregation?.totalInvoiceTotalIncTax
        )
      }
    },

    totalTenantOwnerSupplierChargeIncTax() {
      return this.feesAggregation?.totalTenantOwnerSupplierChargeIncTax
    },
    totalPlatformFeeIncTax() {
      return this.feesAggregation?.totalPlatformFeeIncTax
    },
    totalCreditReturnFeeIncTax() {
      return this.feesAggregation?.totalCreditReturnFeeIncTax
    },
    totalInvoiceTotalIncTax() {
      return this.feesAggregation?.totalInvoiceTotalIncTax
    },

    noSearchCriteria() {
      return !(
        this.queryObject.searchText ||
        this.queryObject.startDate ||
        this.queryObject.endDate
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification', 'removeNotification']),

    formatCurrency(value) {
      return this.$options.filters.currency(value)
    },

    fetch(query) {
      this.neverFetched = false
      this.fetching = true

      fetchFees(query)
        .then((response) => {
          this.feesAggregation = response
          this.fees = response.fees
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetching = false
        })
    },

    fetchInvoicePDF(type, id) {
      this.fetchingInvoicePDF = true
      let fetchFunction
      switch (type) {
        case 'SUPPLIER_ORDER':
          fetchFunction = fetchSupplierOrderInvoicePdfUrl
          break
        case 'CREDIT_RETURN':
          fetchFunction = fetchCreditReturnInvoicePdfUrl
          break
        case 'SUPPLIER_ORDER_QUERY':
          fetchFunction = fetchOrderQueryInvoicePdfUrl
          break
      }

      fetchFunction(id)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `${type} ${id} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingInvoicePDF = false
        })
    },

    searchQuery(queryObject) {
      this.queryObject = queryObject
      if (this.noSearchCriteria) {
        this.confirmationDialog = true
      } else {
        this.doSearch()
      }
    },

    doSearch() {
      this.confirmationDialog = false
      this.feesAggregation = null
      this.fees = []
      this.fetch(this.queryObject.query)
    },

    createLinkForFee(fee) {
      const objectId = fee.objectId.toString()
      switch (fee.type) {
        case 'SUPPLIER_ORDER':
          return {
            name: 'purchase-order-details',
            params: { id: objectId }
          }

        case 'CREDIT_RETURN':
          return {
            name: 'credit-return-details',
            params: { id: objectId }
          }
        case 'SUPPLIER_ORDER_QUERY':
          return {
            name: 'order-query-details',
            params: { id: objectId }
          }
      }
    },

    fetchExcel(queryObject) {
      this.fetchingExcel = true
      this.addNotification({
        color: 'secondary',
        text: `Exporting data can take a while, please wait...`,
        timeout: -1
      })

      fetchFeesExcel(queryObject)
        .then((response) => {
          this.removeNotification()
          this.generateFileDownload({
            response
          })
        })
        .catch((error) => {
          this.notifyDownloadError(error)
        })
        .finally(() => {
          this.fetchingExcel = false
        })
    },

    generateFileDownload(payload) {
      const { response } = payload

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const downloadDate = moment().format(
        COMMON_CONSTANTS.DATE_FORMAT_FOR_DOWNLOAD_FILE_NAME
      )

      const link = document.createElement('a')

      link.href = window.URL.createObjectURL(blob)
      link.download = `fees_${downloadDate}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    notifyDownloadError(error) {
      error.response.status === 404
        ? this.addNotification({
            color: 'error',
            text: `Results could not be exported`
          })
        : this.addNotification({
            color: 'error',
            defaultError: true
          })
    }
  },

  mounted() {}
}
</script>

<style lang="scss" scoped>
#fees {
  .p-report-table {
    tr:last-child td {
      background-color: $primary-color-light !important;
      border-top: 2px solid $primary-color;
      font-weight: bold;
      text-align: left;
      font-size: 1em;
    }
  }

  .totals-card {
    background-color: $primary-color-light !important;
    border: 2px solid $primary-color;
  }

  .p-btn-pdf {
    color: $link-color;

    &:before {
      font-size: 32px;
    }
  }
}

@media only screen and (min-width: 1264px) {
  #fees {
    .p-btn {
      width: 125px;
    }
  }
}
</style>
